import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';

type Ticket = {
  id: string; ticketNo: string; subject: string; status: string; priority: string;
  channel: string; productCode?: string; updatedAt: string;
  assignee?: { name: string } | null;
  endCustomer?: { name: string; email: string } | null;
  _count?: { replies: number };
};

const STATUSES = ['NEW', 'OPEN', 'IN_PROGRESS', 'WAITING_ON_CUSTOMER', 'ON_HOLD', 'RESOLVED', 'CLOSED'];
const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'CRITICAL'];

const STATUS_BADGE: Record<string, string> = {
  NEW: 'bg-blue-100 text-blue-700',
  OPEN: 'bg-emerald-100 text-emerald-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-700',
  WAITING_ON_CUSTOMER: 'bg-purple-100 text-purple-700',
  ON_HOLD: 'bg-slate-100 text-slate-700',
  RESOLVED: 'bg-green-100 text-green-700',
  CLOSED: 'bg-slate-200 text-slate-600',
  CANCELLED: 'bg-rose-100 text-rose-700',
};

const PRI_BADGE: Record<string, string> = {
  LOW: 'bg-slate-100 text-slate-600',
  NORMAL: 'bg-blue-100 text-blue-700',
  HIGH: 'bg-amber-100 text-amber-800',
  CRITICAL: 'bg-rose-100 text-rose-700',
};

export default function TicketsPage() {
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const qc = useQueryClient();

  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ['tickets', { status, priority, search }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (priority) params.set('priority', priority);
      if (search) params.set('search', search);
      return (await api.get(`/tickets?${params}`)).data as Ticket[];
    },
  });

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Tickets</h1>
          <p className="text-sm text-slate-500">{tickets.length} shown</p>
        </div>
        <button className="btn-primary" onClick={() => setShowNew(true)}>+ New ticket</button>
      </div>

      <div className="card p-3 mb-4 flex gap-2 flex-wrap">
        <input className="input max-w-xs" placeholder="Search subject, no, email…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="input max-w-[170px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="input max-w-[150px]" value={priority} onChange={(e) => setPriority(e.target.value)}>
          <option value="">All priorities</option>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {isLoading ? (
        <div className="card p-6 text-slate-500">Loading…</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Ticket</th>
                <th className="text-left px-4 py-3">Subject</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Assignee</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tickets.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">
                    <Link to={`/tickets/${t.id}`} className="text-brand-700 hover:underline">{t.ticketNo}</Link>
                  </td>
                  <td className="px-4 py-3"><Link to={`/tickets/${t.id}`} className="text-slate-900 hover:underline">{t.subject}</Link>{t.productCode && <span className="ml-2 badge bg-slate-100 text-slate-600 text-[10px]">{t.productCode}</span>}</td>
                  <td className="px-4 py-3 text-slate-600">{t.endCustomer?.name ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{t.assignee?.name ?? '—'}</td>
                  <td className="px-4 py-3"><span className={'badge ' + (STATUS_BADGE[t.status] ?? 'bg-slate-100')}>{t.status}</span></td>
                  <td className="px-4 py-3"><span className={'badge ' + (PRI_BADGE[t.priority] ?? 'bg-slate-100')}>{t.priority}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-500">{new Date(t.updatedAt).toLocaleString()}</td>
                </tr>
              ))}
              {tickets.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">No tickets match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NewTicketModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); qc.invalidateQueries({ queryKey: ['tickets'] }); }} />}
    </div>
  );
}

function NewTicketModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('NORMAL');
  const [contactEmail, setContactEmail] = useState('');
  const [contactName, setContactName] = useState('');

  const create = useMutation({
    mutationFn: async () => api.post('/tickets', { subject, description, priority, contactEmail: contactEmail || undefined, contactName: contactName || undefined, channel: 'WEB' }),
    onSuccess: () => { toast.success('Ticket created'); onCreated(); },
    onError: (e: { response?: { data?: { error?: string } } }) => toast.error(e.response?.data?.error ?? 'Failed'),
  });

  return (
    <div className="fixed inset-0 bg-slate-900/40 grid place-items-center p-4 z-50">
      <div className="card w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-4">New ticket</h2>
        <div className="space-y-3">
          <div><label className="label">Subject</label><input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} /></div>
          <div><label className="label">Description</label><textarea className="input min-h-[100px]" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Priority</label>
              <select className="input" value={priority} onChange={(e) => setPriority(e.target.value)}>
                {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div><label className="label">Contact email</label><input className="input" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} /></div>
          </div>
          <div><label className="label">Contact name</label><input className="input" value={contactName} onChange={(e) => setContactName(e.target.value)} /></div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!subject || create.isPending} onClick={() => create.mutate()}>{create.isPending ? 'Creating…' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}
